/* eslint-disable max-lines, max-lines-per-function, complexity, react-perf/jsx-no-new-array-as-prop, react-perf/jsx-no-new-function-as-prop, react-perf/jsx-no-new-object-as-prop -- TODO: Refactor ChatTopMenu to reduce file size and complexity */
import {
  AnalyticsEvents,
  CHARACTER_VISIBILITY,
  CharacterType,
  CHAT_TOP_MENU_ACTIONS,
  checkIsGroupCharacter,
  ICharacter,
  IConversationMessage,
  NotificationType,
  TypingMode,
  useAppSelector,
  useTranslation,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import {
  CapabilityGate,
  CapabilityKey,
  SubCapabilityKey,
  useApplicationCapabilities,
  useApplicationCapabilitiesAllowed,
} from "@next-platform/client-common-core/infra/application/capabilities";
import {
  ButtonIcon,
  ButtonSize,
  ButtonVariant,
  DropDownMenu,
  DropDownMenuDividerPositions,
  DropDownMenuItem,
  IconVariants,
  Text,
  TextVariants,
  useMedia,
} from "@next-platform/client-common-spicy-ui";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { BlockCreatorModal, CapabilityShareCharacterModal } from "@/components";
import AvatarWithModal from "@/components/common/AvatarWithModal/AvatarWithModal";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import NavigateBackButtonV2 from "@/components/common/NavigateBackButtonV2";
import ModalConversationMemories from "@/components/ConversationMemories/ModalConversationMemories";
import { Col, Row } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import ConversationRulebookPicker from "@/components/Rulebook/ConversationRulebookPicker";
import useConversationRulebookMenuItem from "@/components/Rulebook/useConversationRulebookMenuItem";
import useRulebookHudMenuItem from "@/components/Rulebook/useRulebookHudMenuItem";
import useApi from "@/components/useApi";
import { CHAT_PAGE_WIDTH, FEATURE_FLAGS, QueueStatus, SPICYCHAT_USER_PERMISSIONS } from "@/constants";
import getUserPlan from "@/features/user/utils/getUserPlan";
import { useFeatureFlag, useToast } from "@/hooks";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import ContextCapIndicator from "@/pages/Chat/components/ContextCapUpsell/ContextCapIndicator";
import { ModalGenerationSettings } from "@/pages/Chat/components/ModalGenerationSettings";
import ModelSelectionDropdown from "@/pages/Chat/components/ModelSelectionDropdown/ModelSelectionDropdown";
import { ChatPageModal } from "@/pages/Chat/components/types";
import UpdateCloneTitleModal from "@/pages/Chat/components/UpdateCloneTitleModal";
import ChatMessageAudioContainer from "@/pages/Chat/components/voices/ChatMessageAudioContainer";
import { PREFERRED_VOICE_ACTIONS } from "@/pages/Chat/components/voices/constant";
import { IhandlePreferredVoiceActions } from "@/pages/Chat/components/voices/types";
import shouldHideAutoMemoryMenuItem from "@/pages/Chat/utils/shouldHideAutoMemoryMenuItem";
import shouldHideEditCharacterMenuItem from "@/pages/Chat/utils/shouldHideEditCharacterMenuItem";
import useAuth from "@/services/useAuth";
import { getBusinessRouteScreen, getProfilePathForCharacter, showMaxFavoriteCharacterError } from "@/utils";
import { getIsPartner } from "@/utils/getIsPartner";

interface IChatTopMenuProps {
  onNewConversation: () => void;
  onClearConversationErrors?: () => void;
  character: ICharacter;
  onRemoveChat: () => void;
  isShowRemove: boolean;
  conversationLength: number;
  setErrorModalVisible: (visible: boolean) => void;
  setErrorModalMessage: (message: string) => void;
  isBotAccessAllowed: boolean;
  conversation: IConversationMessage[];
  altMsgs: IConversationMessage[];
  conversationId: string;
  handleCloneTitleUpdate: (title: string) => Promise<void>;
  conversationLabel: string;
  setIsChangePersona: (isOpen: boolean) => void;
  setIsNewConversation: (isNew: boolean) => void;
  isDoNotShowChoosePersonaChecked: boolean;
  setSelectedPersonaIds: (ids: string[]) => void;
  selectedPersonaId: string | undefined;
  onUserStartCloning: (conversation: IConversationMessage[]) => void;
  voiceId: string;
  handlePreferredVoiceActions: (args: Partial<IhandlePreferredVoiceActions>) => void;
  showVoiceSelectionModal: boolean;
  showShare: boolean;
  handleAutoResponseUpdate: () => void;
  isAutoResponseActivated: boolean;
  showModelSettings: boolean;
  showBlockModal: boolean;
  isUpdateCloneTitleModal: boolean;
  isShowConversationMemoryModal: boolean;
  isShowChangePersonaConfirmationModal: boolean;
  showChatbotRatingModal: boolean;
  handleModalVisibility: (modal: ChatPageModal, isOpen: boolean) => void;
  defaultPersonaId: string;
  isChatWithGroup?: boolean;
  typingMode: TypingMode | null;
  isStoryMode?: boolean;
  /** undefined = not loaded yet, null = confirmed not capped, string = capped */
  cappedMessageId?: string | null | undefined;
  isCutoffMessageLoaded?: boolean;
  onJumpToCutoff?: () => void;
}

const ChatTopMenu = ({
  onNewConversation = () => {},
  onClearConversationErrors = () => {},
  character = {} as ICharacter,
  onRemoveChat = () => {},
  isShowRemove = false,
  setErrorModalVisible = () => {},
  setErrorModalMessage = () => {},
  isBotAccessAllowed,
  conversation = [],
  altMsgs = [],
  conversationId = "",
  handleCloneTitleUpdate = async () => {},
  conversationLabel = "",
  setIsChangePersona = () => {},
  setIsNewConversation = () => {},
  isDoNotShowChoosePersonaChecked = false,
  setSelectedPersonaIds = () => {},
  handleAutoResponseUpdate,
  isAutoResponseActivated,
  selectedPersonaId = "",
  onUserStartCloning = () => {},
  voiceId = "",
  handlePreferredVoiceActions = () => {},
  showVoiceSelectionModal = false,
  showShare = false,
  showModelSettings = false,
  showBlockModal = false,
  isUpdateCloneTitleModal = false,
  isShowConversationMemoryModal = false,
  isShowChangePersonaConfirmationModal = false,
  handleModalVisibility = () => {},
  defaultPersonaId,
  isChatWithGroup,
  typingMode,
  isStoryMode = false,
  cappedMessageId,
  isCutoffMessageLoaded = false,
  onJumpToCutoff,
}: IChatTopMenuProps) => {
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const { logEvent } = useQueueLogEvent();
  const navigate = useNavigate();
  const { isDesktop } = useMedia();
  const { characterId } = useParams();
  const { pagePath } = useLocalizedLocation();
  const screenName = getBusinessRouteScreen(pagePath);
  const { errorData } = useApi();
  const { permissions, onlyUserId: user_id } = useUserInfo();
  const ttsCapability = useApplicationCapabilities(CapabilityKey.Tts);
  const isTtsAllowed = ttsCapability?.allowed ?? false;
  const isTtsUiVisible = Boolean(ttsCapability?.allowed || ttsCapability?.isPaywall);
  const { hasFeatureFlag: hasVoiceAttachmentFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.CHARACTER_VOICE_ATTACHMENT);
  const { hasFeatureFlag: hasContextCapUpsell } = useFeatureFlag(FEATURE_FLAGS.CONTEXT_CAP_UPSELL);
  const { likedBots, blockedUsers, onUserLikebot, blurNSFW, isAutoMemoryEnabled, onUserSetAutoMemory } = useUserSettings();
  const [stateLiked, setStateLiked] = useState(false);
  const { status: waitingQueueStatus } = useAppSelector(({ waitingQueue }) => waitingQueue) || {};
  const enableEditableChatExperience = waitingQueueStatus !== QueueStatus.WAITING;
  const isCharacterOwner = character?.creator_user_id === user_id;
  const isPublicOrHidden = character?.visibility === CHARACTER_VISIBILITY.PUBLIC || character?.visibility === CHARACTER_VISIBILITY.HIDDEN;
  const { isAuthenticated } = useAuth();
  const isBotCreatorBlocked = blockedUsers?.includes(character?.creator_user_id);
  const isPartner = getIsPartner();
  const isCharacterCreator = user_id && user_id === character?.creator_user_id;
  const isRatingWidgetVisible = character?.visibility === "public" && user_id;
  const isConversationId = conversation?.some((convo) => convo?.conversation_id);
  const { isUserPremium } = getUserPlan();

  const hasConversationMemoryPermission = permissions?.some((permission: string) =>
    [SPICYCHAT_USER_PERMISSIONS.memory_manager, SPICYCHAT_USER_PERMISSIONS.conversation_memory_auto].includes(permission),
  );
  const isManageConversationMemoryVisible = isConversationId && hasConversationMemoryPermission;

  const isCharacterAvatarBlur = (character?.avatar_is_nsfw && blurNSFW) || (!isAuthenticated && character?.is_nsfw);

  const { hasFeatureFlag: hasStoryModeFlag, isLoading: storyModeFlagIsLoading } = useFeatureFlag(FEATURE_FLAGS.STORYMODE);
  const hasStoryMode = hasStoryModeFlag && !storyModeFlagIsLoading;
  const isGroupCharacter = checkIsGroupCharacter({ character });
  const rulebookMenu = useConversationRulebookMenuItem({
    conversationId,
    conversation,
    altMsgs,
    isAvailable: isAuthenticated && !isChatWithGroup && !isGroupCharacter,
  });
  const rulebookHudMenuItem = useRulebookHudMenuItem({
    characterId: character?.id,
    conversationId,
    isAvailable: isAuthenticated && !isChatWithGroup && !isGroupCharacter,
  });
  const [
    isBlockCreatorsAllowed,
    isGenerationSettingsAllowed,
    isStoryModeAllowed,
    isMemoryManagerAllowed,
    isCharacterShareLinkAllowed,
    isGroupChatManageAllowed,
  ] = useApplicationCapabilitiesAllowed([
    SubCapabilityKey.DiscoverBlockCreators,
    SubCapabilityKey.ChatGenerationSettings,
    SubCapabilityKey.ChatStoryMode,
    SubCapabilityKey.ChatMemoryManager,
    SubCapabilityKey.CharacterShareLink,
    SubCapabilityKey.GroupChatManage,
  ]);

  useEffect(() => {
    if (likedBots && character) {
      setStateLiked((likedBots || [])?.includes(character?.id));
    }
  }, [likedBots, character]);

  useEffect(() => {
    showMaxFavoriteCharacterError({
      errorData,
      setErrorModalMessage,
      setErrorModalVisible,
      setLiked: setStateLiked,
    });
  }, [errorData, setErrorModalMessage, setErrorModalVisible, setStateLiked]);

  const handleProfileNavigation = () => {
    return getProfilePathForCharacter(character);
  };

  const handleDropdownActions = async (action: string) => {
    document.body.style.overflow = "";
    handlePreferredVoiceActions({ action: PREFERRED_VOICE_ACTIONS.Stop });
    switch (action) {
      case CHAT_TOP_MENU_ACTIONS.START_NEW_CHAT: {
        onClearConversationErrors();
        if (!isAuthenticated || isDoNotShowChoosePersonaChecked) {
          onNewConversation();
        } else {
          setSelectedPersonaIds([selectedPersonaId]);
          setIsNewConversation(true);
          setIsChangePersona(true);
        }
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.VIEW_SAVED_CHAT: {
        navigate(`/chats/${characterId}`);
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.SWITCH_STORYMODE: {
        navigate(`/story/${characterId}${conversationId ? `/${conversationId}` : ""}`);
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.SWITCH_CHATMODE: {
        navigate(`/chat/${characterId}${conversationId ? `/${conversationId}` : ""}`);
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.REMOVE_MESSAGES: {
        onRemoveChat();
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.CHANGE_PERSONA: {
        if (conversationId) {
          handleModalVisibility(ChatPageModal.CHANGE_PERSONA_CONFIRMATION_MODAL, true);
        } else {
          setIsChangePersona(true);
        }
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.CLONE_CONVERSATION: {
        onUserStartCloning(conversation.concat(altMsgs) as unknown as IConversationMessage[]);
        handleModalVisibility(ChatPageModal.CLONE_CONVERSATION_MODAL, true);
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.CHANGE_TITLE: {
        handleModalVisibility(ChatPageModal.UPDATE_CLONE_TITLE_MODAL, true);
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.CHANGE_VOICE: {
        if (ttsCapability?.isPaywall) {
          handleModalVisibility(ChatPageModal.SHOW_VOICE_UPGRADE_MODAL, true);
          break;
        }
        if (!isTtsAllowed) {
          break;
        }
        handleModalVisibility(ChatPageModal.SHOW_VOICE_SELECION_MODAL, true);
        handlePreferredVoiceActions({
          action: PREFERRED_VOICE_ACTIONS.Get_Voices,
          errorCB: () => handleModalVisibility(ChatPageModal.SHOW_VOICE_SELECION_MODAL, false),
        });
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.LIKE_UNLIKE_BOT: {
        const likedBot = character?.id;
        await onUserLikebot(likedBot, screenName!);
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.VIEW_BOT_PROFILE: {
        navigate(handleProfileNavigation());
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.EDIT_CHATBOT: {
        navigate(`/chatbot/edit/${characterId}`, { replace: true });
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.EDIT_GROUP: {
        navigate(`/group/edit/${characterId}`, { replace: true });
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.BLOCK_CREATOR: {
        handleModalVisibility(ChatPageModal.SHOW_BLOCK_MODAL, true);
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.MANAGE_MEMORIES: {
        handleModalVisibility(ChatPageModal.CONVERSATION_MEMORY_MODAL, true);
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.GENERATION_SETTINGS: {
        handleModalVisibility(ChatPageModal.SHOW_MODEL_SETTINGS, true);
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.EDIT_AUTORESPONSE_MODE: {
        handleAutoResponseUpdate();
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.TOGGLE_AUTO_MEMORY: {
        await onUserSetAutoMemory(!isAutoMemoryEnabled);
        displayToast({
          message: isAutoMemoryEnabled
            ? t("chat:page.header.dropdown.toggleAutoMemory.toast.success.disableMemory")
            : t("chat:page.header.dropdown.toggleAutoMemory.toast.success.enableMemory"),
          type: NotificationType.success,
        });
        break;
      }
      case CHAT_TOP_MENU_ACTIONS.UNLOCK_CUSTOM_VOICES: {
        handleModalVisibility(ChatPageModal.SHOW_VOICE_UPGRADE_MODAL, true);
        break;
      }
    }
  };

  const handleClose = useCallback(() => {
    handleModalVisibility(ChatPageModal.SHOW_BLOCK_MODAL, false);
  }, [handleModalVisibility]);

  const handleUserConfirmationToChangePersona = () => {
    setSelectedPersonaIds([selectedPersonaId]);
    setIsChangePersona(true);
    return Promise.resolve(undefined);
  };

  const dropDownMenuItems: DropDownMenuItem[] = [
    {
      icon: IconVariants.MESSAGE_PLUS,
      label: t("chat:page.header.dropdown.startNewChat.title"),
      description: t("chat:page.header.dropdown.startNewChat.description"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.START_NEW_CHAT),
      isHidden: !enableEditableChatExperience,
      isDisabled: !!typingMode,
    },
    {
      icon: IconVariants.FLAME,
      label: t("chat:page.header.dropdown.switchToStoryMode.title"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.SWITCH_STORYMODE),
      isHidden: isStoryMode || !hasStoryMode || !isStoryModeAllowed || isGroupCharacter || isPartner || isGroupCharacter,
      isDisabled: !!typingMode,
    },
    {
      icon: IconVariants.MESSAGE,
      label: t("chat:page.header.dropdown.switchToChatMode.title"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.SWITCH_CHATMODE),
      isHidden: !isStoryMode || isPartner || !hasStoryMode,
      isDisabled: !!typingMode,
    },
    ...(isPublicOrHidden && !isBotCreatorBlocked && isCharacterShareLinkAllowed
      ? [
          {
            icon: IconVariants.SHARE,
            label: t("chat:page.header.dropdown.shareChatbot.title"),
            onPress: () => handleShowShare(),
          },
        ]
      : []),
    {
      icon: IconVariants.LOGS,
      label: t("chat:page.header.dropdown.viewSavedChats.title"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.VIEW_SAVED_CHAT),
      replace: true,
    },
    {
      icon: IconVariants.TRASH,
      label: t("chat:page.header.dropdown.removeMessages.title"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.REMOVE_MESSAGES),
      isHidden: !isShowRemove || !enableEditableChatExperience,
    },
    {
      icon: IconVariants.VENETIAN_MASK,
      label: t("chat:page.header.dropdown.changePersona.title"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.CHANGE_PERSONA),
      isHidden: !isAuthenticated,
    },
    rulebookMenu.menuItem,
    rulebookHudMenuItem,
    {
      icon: IconVariants.COPY,
      label: t("chat:page.header.dropdown.cloneConversation.title"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.CLONE_CONVERSATION),
      isHidden: !isShowRemove,
    },
    {
      icon: IconVariants.EDIT,
      label: t("chat:page.header.dropdown.changeTitle.title"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.CHANGE_TITLE),
      isHidden: !isConversationId,
    },
    {
      icon: IconVariants.FAVORITE,
      label: stateLiked ? t("chat:page.header.dropdown.removeBotFromFavorites.title") : t("chat:page.header.dropdown.addBotToFavorite.title"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.LIKE_UNLIKE_BOT),
      isHidden: !isAuthenticated || !!isCharacterCreator,
    },
    ...(!isPartner && character?.sub_characters && character.sub_characters?.length
      ? [
          {
            icon: IconVariants.SKIP_FORWARD,
            label: isAutoResponseActivated
              ? t("chat:page.header.dropdown.disableAutoResponse.title")
              : t("chat:page.header.dropdown.enableAutoResponse.title"),
            description: isAutoResponseActivated
              ? t("chat:page.header.dropdown.disableAutoResponse.description")
              : t("chat:page.header.dropdown.enableAutoResponse.description"),
            onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.EDIT_AUTORESPONSE_MODE),
            replace: true,
          },
        ]
      : []),
    {
      icon: IconVariants.BAN,
      label: t("chat:page.header.dropdown.blockCreator.title"),
      description: t("chat:page.header.dropdown.blockCreator.description"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.BLOCK_CREATOR),
      dataTestId: "chat-block-creator-menu-item",
      isHidden: isCharacterOwner || !isAuthenticated || !isBlockCreatorsAllowed,
    },
    {
      icon: IconVariants.EDIT,
      label:
        character?.type === CharacterType.STANDARD
          ? t("chat:page.header.dropdown.editChatbot.title")
          : t("chat:page.header.dropdown.editGroup.title"),
      onPress: () =>
        handleDropdownActions(character?.type === CharacterType.STANDARD ? CHAT_TOP_MENU_ACTIONS.EDIT_CHATBOT : CHAT_TOP_MENU_ACTIONS.EDIT_GROUP),
      dataTestId: "chat-edit-character-menu-item",
      isHidden: shouldHideEditCharacterMenuItem({ isCharacterOwner, isGroupCharacter, isGroupChatManageAllowed }),
    },
    {
      icon: IconVariants.VOLUME_2,
      label: voiceId ? t("chat:page.header.dropdown.voiceChange.title") : t("chat:page.header.dropdown.setVoice.title"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.CHANGE_VOICE),
      isHidden: !conversationId || !isTtsUiVisible || isChatWithGroup,
    },
    {
      icon: IconVariants.VOLUME_2,
      label: t("chat:page.header.dropdown.unlockCustomVoices.title"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.UNLOCK_CUSTOM_VOICES),
      isHidden: isUserPremium || !hasVoiceAttachmentFeatureFlag || isChatWithGroup,
    },
    {
      icon: IconVariants.SAVE,
      label: isAutoMemoryEnabled
        ? t("chat:page.header.dropdown.toggleAutoMemory.disableMemory")
        : t("chat:page.header.dropdown.toggleAutoMemory.enableMemory"),
      description: t("chat:page.header.dropdown.toggleAutoMemory.description"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.TOGGLE_AUTO_MEMORY),
      isHidden: shouldHideAutoMemoryMenuItem({ hasConversationMemoryPermission, isMemoryManagerAllowed }),
      divider: DropDownMenuDividerPositions.Top,
    },
    {
      icon: IconVariants.BOOK_TEXT,
      label: t("chat:page.header.dropdown.manageMemories.title"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.MANAGE_MEMORIES),
      dataTestId: "chat-memory-manager-menu-item",
      isHidden: !isManageConversationMemoryVisible || !isMemoryManagerAllowed,
    },
    {
      icon: IconVariants.SLIDER_VERTICAL,
      label: t("chat:page.header.dropdown.generationSettings.title"),
      onPress: () => handleDropdownActions(CHAT_TOP_MENU_ACTIONS.GENERATION_SETTINGS),
      dataTestId: "chat-gen-settings-menu-item",
      isHidden: isPartner || !isGenerationSettingsAllowed,
    },
  ];

  const handleChatbotInteractionEvents = (event: AnalyticsEvents) => {
    logEvent(event, {
      character_id: characterId,
      page_name: screenName,
    });
  };

  const handleShowShare = () => {
    handlePreferredVoiceActions({ action: PREFERRED_VOICE_ACTIONS.Stop });
    handleModalVisibility(ChatPageModal.SHOW_SHARE, true);
    handleChatbotInteractionEvents(AnalyticsEvents.CHATBOT_SHARE);
  };
  return (
    <Row className="self-center w-full min-h-[56px] items-center" style={{ maxWidth: CHAT_PAGE_WIDTH }}>
      <Col>
        <Row className="items-center gap-3">
          {!isDesktop && <NavigateBackButtonV2 withLabel={false} />}
          <Row className="items-center gap-3">
            <div>
              <AvatarWithModal src={character?.avatar_url || ""} name={character?.name} blurred={isCharacterAvatarBlur} />
            </div>
            <Col>
              <LocalizedLink
                to={handleProfileNavigation()}
                onClick={() => handleChatbotInteractionEvents(AnalyticsEvents.CHATBOT_DESCRIPTION)}
                aria-label="chatbot-profile"
              >
                <Text className="text-gray-12 break-all line-clamp-1 my-0 tracking-normal" variant={TextVariants.HEADING_6_BOLD} as="h1">
                  {character?.name}
                </Text>
              </LocalizedLink>
              <LocalizedLink to={`/creator/${character?.creator_username}`} aria-label="creator-profile">
                <Text className="text-gray-11 break-all line-clamp-1" variant={TextVariants.PARAGRAPH_XSMALL_REGULAR}>
                  @{character?.creator_username}
                </Text>
              </LocalizedLink>
            </Col>
          </Row>
        </Row>
      </Col>
      <Row justify="end" align="center" className="gap-sm">
        {/* Context Cap Indicator - Shows when conversation is capped */}
        {!isPartner && hasContextCapUpsell ? (
          <CapabilityGate capability={SubCapabilityKey.ChatContextCap}>
            <ContextCapIndicator
              conversationId={conversationId}
              cappedMessageId={cappedMessageId}
              isCutoffMessageLoaded={isCutoffMessageLoaded}
              onJumpToCutoff={onJumpToCutoff}
            />
          </CapabilityGate>
        ) : null}
        {isRatingWidgetVisible && !isBotCreatorBlocked ? (
          <ButtonIcon
            icon={IconVariants.LIKE}
            variant={ButtonVariant.OUTLINED}
            size={ButtonSize.MEDIUM}
            onClick={() => {
              handlePreferredVoiceActions({
                action: PREFERRED_VOICE_ACTIONS.Stop,
              });
              handleModalVisibility(ChatPageModal.CHATBOT_RATING_MODAL, true);
            }}
          />
        ) : null}
        {!isPartner ? (
          <CapabilityGate capability={SubCapabilityKey.ChatModelTier}>
            <ModelSelectionDropdown dataTestId="ChatModelTierCapabilityGate" handleModalVisibility={handleModalVisibility} />
          </CapabilityGate>
        ) : null}
        {!isBotCreatorBlocked && isBotAccessAllowed ? (
          <DropDownMenu
            listItems={dropDownMenuItems}
            placement="bottom-end"
            className="min-w-[252px]"
            classNames={{
              itemsDescription: "!text-label-sm",
            }}
          >
            <ButtonIcon variant={ButtonVariant.OUTLINED} icon={IconVariants.MORE_HORIZONTAL} size={ButtonSize.MEDIUM} aria-label="chat-dropdown" />
          </DropDownMenu>
        ) : null}
      </Row>
      <CapabilityShareCharacterModal
        visible={showShare}
        setShowShare={(isShowShare) => handleModalVisibility(ChatPageModal.SHOW_SHARE, isShowShare)}
        characterId={characterId}
        characterName={character.name}
      />
      {showModelSettings ? (
        <CapabilityGate capability={SubCapabilityKey.ChatGenerationSettings}>
          <ModalGenerationSettings
            dataTestId="ChatGenerationSettingsCapabilityGate"
            isOpen={showModelSettings}
            onClose={() => handleModalVisibility(ChatPageModal.SHOW_MODEL_SETTINGS, false)}
          />
        </CapabilityGate>
      ) : null}

      {showBlockModal ? (
        <CapabilityGate capability={SubCapabilityKey.DiscoverBlockCreators}>
          <BlockCreatorModal
            dataTestId="DiscoverBlockCreatorsCapabilityGate"
            creatorId={character?.creator_user_id as string}
            creatorUsername={character?.creator_username as string}
            isVisible={showBlockModal}
            onClose={handleClose}
            onSuccess={() => navigate(`/`)}
          />
        </CapabilityGate>
      ) : null}
      {isUpdateCloneTitleModal ? (
        <UpdateCloneTitleModal
          isVisible={isUpdateCloneTitleModal}
          setIsUpdateCloneTitleModal={(isShow) => handleModalVisibility(ChatPageModal.UPDATE_CLONE_TITLE_MODAL, isShow)}
          handleCloneTitleUpdate={handleCloneTitleUpdate}
          conversationLabel={conversationLabel}
          characterName={character?.name}
        />
      ) : null}
      {isShowConversationMemoryModal ? (
        <CapabilityGate capability={SubCapabilityKey.ChatMemoryManager}>
          <ModalConversationMemories
            dataTestId="ChatMemoryManagerCapabilityGate"
            conversationId={conversationId}
            isOpen={isShowConversationMemoryModal}
            setIsOpen={(isShow) => handleModalVisibility(ChatPageModal.CONVERSATION_MEMORY_MODAL, isShow)}
          />
        </CapabilityGate>
      ) : null}

      {isShowChangePersonaConfirmationModal ? (
        <ConfirmationModal
          isLoading={false}
          heading={t("chat:modal.changePersona.title")}
          confirmationMessage={t("chat:modal.changePersona.description")}
          isVisible={isShowChangePersonaConfirmationModal}
          onUserConfirmation={handleUserConfirmationToChangePersona}
          handleClose={() => handleModalVisibility(ChatPageModal.CHANGE_PERSONA_CONFIRMATION_MODAL, false)}
        />
      ) : null}
      {rulebookMenu.isPickerOpen ? (
        <ConversationRulebookPicker conversationId={conversationId} isOpen={rulebookMenu.isPickerOpen} onClose={rulebookMenu.closePicker} />
      ) : null}
      {showVoiceSelectionModal ? (
        <ChatMessageAudioContainer
          voiceId={voiceId}
          conversationId={conversationId}
          showVoiceSelectionModal={showVoiceSelectionModal}
          setShowVoiceSelectionModal={(isShow) => handleModalVisibility(ChatPageModal.SHOW_VOICE_SELECION_MODAL, isShow)}
          handlePreferredVoiceActions={handlePreferredVoiceActions}
          characterId={characterId || ""}
          defaultPersonaId={defaultPersonaId}
          characterDefaultVoiceId={character?.voice_id ?? null}
        />
      ) : null}
    </Row>
  );
};

export default ChatTopMenu;
